<template>
    <div>
        <div class="flex cursor-pointer items-center py-1.5" @click="handleCheckAllChange">
            <input :disabled="disabledAllCheck" type="checkbox" :checked="checkAll"
                   class="th-checkbox pointer-events-none mx-1" :indeterminate="isIndeterminate" />
            <span class="ml-1 text-sm">{{ allLabel }}</span>
        </div>

        <template v-for="(item, index) in allLegals" :key="index">
            <ElTooltip 
                v-if="item.disabled && $slots.tooltip"
                :disabled="!item.disabled"
                placement="top-start"
                effect="light"
                :offset="0"
                :visible-arrow="false"
                popper-class="pt-ui-tooltip"
            >
                <template #content>
                    <slot name="tooltip" :data="item" />
                </template>
                <div class="flex cursor-pointer items-center gap-1 py-1.5 pl-6" 
                     :class="{ 'cursor-not-allowed opacity-50': item.disabled }"
                     @click="handleCheckedChange(item)">
                    <input
                        type="checkbox"
                        :value="item.id"
                        :checked="item.checked"
                        :disabled="item.disabled"
                        class="th-checkbox pointer-events-none mx-1"
                    />
                    <slot name="icon-before-text" :data="item" />
                    <span class="text-sm">{{ item.label }}</span>
                </div>
            </ElTooltip>
            
            <div v-else class="flex cursor-pointer items-center gap-1 py-1.5 pl-6" 
                 :class="{ 'cursor-not-allowed opacity-50': item.disabled }"
                 @click="handleCheckedChange(item)">
                <input
                    type="checkbox"
                    :value="item.id"
                    :checked="item.checked"
                    :disabled="item.disabled"
                    class="th-checkbox pointer-events-none mx-1"
                />
                <slot name="icon-before-text" :data="item" />
                <span class="text-sm">{{ item.label }}</span>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
import type { ISelectData } from './types'
import { watch, ref, computed, reactive } from 'vue'
import { ElTooltip } from 'element-plus'
import _ from 'lodash';

const props = defineProps<{
    modelValue: (string | number)[],
    listData: ISelectData[],
    disabledAllCheck?: boolean,
    allLabel: string
}>()

let allLegals = reactive<ISelectData[]>([])
const checkedContent = ref<(number| string)[]>([])

const checkAll = computed(() => {
    return checkedContent.value.length === props.listData.length
})

const isIndeterminate = computed(() => {
    return checkedContent.value.length > 0 && checkedContent.value.length < props.listData.length
})

const emits = defineEmits<{
    (e: 'update:modelValue', id: (string | number)[]): void
}>()

function handleCheckedChange(choose: ISelectData) {
    // 如果项目被禁用，不允许点击
    if (choose.disabled) return
    
    choose.checked = !choose.checked;
    if (choose.checked) {
        checkedContent.value.push(choose.id);
    } else {
        checkedContent.value = checkedContent.value.filter((l) => l !== choose.id);
    }
}

function handleCheckAllChange() {
    if (props.disabledAllCheck) return
    if (checkAll.value) {
        checkedContent.value = []
    } else {
        // 只选择未被禁用的项目
        checkedContent.value = _.cloneDeep(allLegals.filter(item => !item.disabled).map(a => a.id)) as (string | number)[]
    }
}

watch(() => props.modelValue, (v) => {
    checkedContent.value = v
    allLegals.forEach(c => {
        if (checkedContent.value.includes(c.id as number)) {
            c.checked = true
        } else {
            c.checked = false
        }
    })
}, {
    immediate: true,
    deep: true,
})

watch(() => props.listData, (v) => {
    allLegals = v
}, {
    immediate: true,
    deep: true,
})

watch(() => checkedContent.value, (v) => {
    emits('update:modelValue', v);
})

</script>
